<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    LoadingState,
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedAgentBotId: null,
      humanHandoffEnabled: false,
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    storedAgentBotId() {
      return this.$store.state.agentBots.agentBotInbox[this.inbox.id];
    },
    storedHumanTakeoverEnabled() {
      return this.$store.getters['agentBots/getHumanHandoffEnabled'](
        this.inbox.id
      );
    },
  },
  watch: {
    storedAgentBotId: {
      immediate: true,
      handler(value) {
        this.selectedAgentBotId = value || null;
      },
    },
    storedHumanTakeoverEnabled: {
      immediate: true,
      handler(value) {
        this.humanHandoffEnabled = value;
      },
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
  },

  methods: {
    async updateActiveAgentBot() {
      try {
        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          botId: this.selectedAgentBotId ? this.selectedAgentBotId : undefined,
          humanHandoffEnabled: this.humanHandoffEnabled,
        });
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'));
      }
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        useAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="flex flex-wrap mx-0"
      @submit.prevent="updateActiveAgentBot"
    >
      <SettingsSection
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
      >
        <label>
          <select v-model="selectedAgentBotId">
            <option value="" disabled selected>
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SELECT_PLACEHOLDER') }}
            </option>
            <option
              v-for="agentBot in agentBots"
              :key="agentBot.id"
              :value="agentBot.id"
            >
              {{ agentBot.name }}
            </option>
          </select>
        </label>
      </SettingsSection>
      <SettingsSection
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.HUMAN_HANDOFF.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.HUMAN_HANDOFF.DESC')"
      >
        <div class="flex items-center gap-3">
          <input
            id="human-handoff-toggle"
            v-model="humanHandoffEnabled"
            type="checkbox"
            class="w-4 h-4 cursor-pointer"
          />
          <label
            for="human-handoff-toggle"
            class="cursor-pointer text-sm text-slate-700 dark:text-slate-200"
          >
            {{ $t('AGENT_BOTS.BOT_CONFIGURATION.HUMAN_HANDOFF.LABEL') }}
          </label>
        </div>
      </SettingsSection>
      <div class="button-container space-x-2 w-full">
        <NextButton
          type="submit"
          :label="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
          :is-loading="uiFlags.isSettingAgentBot"
        />
        <NextButton
          type="button"
          :disabled="!selectedAgentBotId"
          :is-loading="uiFlags.isDisconnecting"
          faded
          ruby
          @click="disconnectBot"
        >
          {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
        </NextButton>
      </div>
    </form>
  </div>
</template>
